import { useCallback, useEffect, useState } from 'react'

import { useUserContext } from '@/contexts/UserContext.tsx'

const PREFIX = '$userStorage'

export const useUserStorage = <T>(key: string, defaultValue: T) => {
  const { user } = useUserContext()

  const getUserSpecificKey = useCallback(() => {
    return user?.id ? `${PREFIX}$${user.id}$${key}` : null
  }, [user?.id, key])

  const readStorage = useCallback((): T => {
    const userSpecificKey = getUserSpecificKey()
    if (!userSpecificKey) {
      return defaultValue
    }
    try {
      const item = localStorage.getItem(userSpecificKey)
      if (item !== null) {
        return JSON.parse(item) as T
      }
      return defaultValue
    } catch (error) {
      console.error(`Error reading localStorage key “${userSpecificKey}”:`, error)
      try {
        localStorage.removeItem(userSpecificKey)
      } catch (removeError) {
        console.error(`Error removing faulty localStorage key “${userSpecificKey}”:`, removeError)
      }
      return defaultValue
    }
  }, [getUserSpecificKey, defaultValue])

  const [value, setValue] = useState<T>(() => readStorage())

  // biome-ignore lint/correctness/useExhaustiveDependencies: This should update when user changes
  useEffect(() => {
    setValue(readStorage())
  }, [user?.id, readStorage])

  const storeValue = useCallback(
    (newValue: T) => {
      const userSpecificKey = getUserSpecificKey()
      if (userSpecificKey) {
        try {
          localStorage.setItem(userSpecificKey, JSON.stringify(newValue))
          setValue(newValue)
        } catch (error) {
          console.error(`Error setting localStorage key “${userSpecificKey}”:`, error)
        }
      } else {
        // Handle case where trying to store without a logged-in user?
        // Maybe just update local state without persisting?
        setValue(newValue)
      }
    },
    [getUserSpecificKey],
  )

  const removeValue = useCallback(() => {
    const userSpecificKey = getUserSpecificKey()
    if (userSpecificKey) {
      try {
        localStorage.removeItem(userSpecificKey)
        setValue(defaultValue)
      } catch (error) {
        console.error(`Error removing localStorage key “${userSpecificKey}”:`, error)
        setValue(defaultValue)
      }
    } else {
      setValue(defaultValue)
    }
  }, [getUserSpecificKey, defaultValue])

  return {
    value,
    storeValue,
    removeValue,
  }
}
